import { useParams, Navigate, useNavigate } from 'react-router-dom'
import { Trash2 } from 'lucide-react'
import { useSections, useTodos, useDeleteSection } from '../api/hooks'
import { SectionView } from '../components/SectionView'
import { useIsMobile } from '../hooks/useIsMobile'
import { MobileTodoItem } from '../components/mobile/MobileTodoItem'
import { TodoInput } from '../components/TodoInput'
import { useCallback, useMemo } from 'react'
import { sortTodos } from '../lib/todoSort'
import { useUIStore } from '../store/ui'
import { useToast } from '../components/primitives/Toast'

export function SectionPage() {
  const { id } = useParams<{ id: string }>()
  const { data: sections = [], isLoading } = useSections()
  const isMobile = useIsMobile()

  const section = !isLoading ? sections.find((s) => s.id === id) : undefined

  if (isLoading) {
    return (
      <div style={{ height: '100%', overflowY: 'auto' }}>
        <div
          style={{
            padding: '24px',
            color: 'var(--color-ink-muted)',
            fontFamily: 'var(--font-serif)',
          }}
        >
          Loading...
        </div>
      </div>
    )
  }

  if (!section) {
    return <Navigate to="/week" replace />
  }

  if (isMobile) {
    return <MobileSectionLayout sectionId={section.id} sectionName={section.name} />
  }

  return (
    <div style={{ height: '100%', overflowY: 'auto' }}>
      <SectionView section={section} />
    </div>
  )
}

function MobileSectionLayout({ sectionId, sectionName }: { sectionId: string; sectionName: string }) {
  const { data: todos = [] } = useTodos({ section_id: sectionId })
  const smartStatusSort = useUIStore((s) => s.smartStatusSort)
  const sortedTodos = useMemo(() => sortTodos(todos, smartStatusSort), [todos, smartStatusSort])
  const deleteSection = useDeleteSection()
  const navigate = useNavigate()
  const { showToast } = useToast()

  const handleDelete = useCallback(() => {
    if (!window.confirm(`Delete section "${sectionName}"?`)) return
    deleteSection.mutate(sectionId, {
      onSuccess: () => navigate('/week'),
      onError: () => showToast('Failed to delete section', 'error'),
    })
  }, [sectionId, sectionName, deleteSection, navigate, showToast])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div style={{ overflowY: 'auto', flex: 1 }}>
        <header style={{ padding: '16px 16px 8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <h1 className="display-serif" style={{ fontSize: '1.75rem', marginBottom: '4px', flex: 1, minWidth: 0 }}>
              {sectionName}
            </h1>
            <button
              onClick={handleDelete}
              aria-label="Delete section"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--color-ink-faint)',
                padding: '6px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Trash2 size={18} strokeWidth={1.5} />
            </button>
          </div>
          <div className="eyebrow">
            {todos.length} item{todos.length !== 1 ? 's' : ''}
          </div>
        </header>

        {sortedTodos.length === 0 && (
          <div
            style={{
              color: 'var(--color-ink-faint)',
              fontFamily: 'var(--font-serif)',
              fontStyle: 'italic',
              fontSize: '0.9375rem',
              padding: '16px',
            }}
          >
            No tasks yet. Add one below.
          </div>
        )}

        {sortedTodos.map((todo) => (
          <MobileTodoItem key={todo.id} todo={todo} />
        ))}
      </div>

      <div
        style={{
          flexShrink: 0,
          borderTop: '1px solid var(--color-border)',
          padding: '8px 12px',
          paddingBottom: 'calc(8px + env(safe-area-inset-bottom))',
          background: 'var(--color-paper)',
        }}
      >
        <TodoInput defaultSectionId={sectionId} todos={todos} placeholder="+ add" />
      </div>
    </div>
  )
}
